import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, ChevronRight, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const ConceptRevised1 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="concept-revised-1">
      {/* Premium Header - Enhanced Contrast */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_blackbird-redesign/artifacts/iuwp2epg_logo-trade-black-svg.svg" 
                alt="Blackbird Trade Group" 
                className="h-14"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-10">
              <Link to="/about" className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors">About</Link>
              <Link to="/services" className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors">Services</Link>
              <Link to="/resources" className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors">Resources</Link>
              <Link to="/blog" className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors">Insights</Link>
              <Link to="/blackbird-ai" className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors">Blackbird AI</Link>
              <Link to="/contact">
                <Button className="bg-slate-700 hover:bg-slate-800 text-white px-7 h-11 text-sm font-semibold shadow-sm">
                  Contact Us
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-gray-800 hover:text-slate-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 bg-white">
              <nav className="flex flex-col space-y-4">
                <Link 
                  to="/about" 
                  className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link 
                  to="/services" 
                  className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link 
                  to="/resources" 
                  className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Resources
                </Link>
                <Link 
                  to="/blog" 
                  className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Insights
                </Link>
                <Link 
                  to="/blackbird-ai" 
                  className="text-sm font-semibold text-gray-800 hover:text-slate-700 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blackbird AI
                </Link>
                <Link 
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="bg-slate-700 hover:bg-slate-800 text-white w-full h-11 text-sm font-semibold">
                    Contact Us
                  </Button>
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero - Vibrant Port Image */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden pt-20">
        {/* Background - Full vibrant image NO overlay */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_blackbird-redesign/artifacts/c7xxbrm7_The-port-of-Singapore.jpg)'
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-8 lg:px-12 py-24 w-full">
          <div className="max-w-3xl">
            {/* Subtle top label with white background for readability */}
            <div className="mb-10 inline-block">
              <span className="text-sm text-gray-700 font-medium tracking-wide bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">International Trade Compliance & Advisory</span>
            </div>
            
            {/* Refined headline with strong text shadow for readability */}
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-10 leading-[1.15] tracking-tight" style={{ textShadow: '0 4px 20px rgba(0, 0, 0, 0.7), 0 2px 8px rgba(0, 0, 0, 0.5)' }}>
              Simplifying Global Trade<br />Compliance
            </h1>
            
            <p className="text-xl lg:text-2xl text-white mb-12 leading-relaxed font-normal max-w-2xl" style={{ textShadow: '0 2px 12px rgba(0, 0, 0, 0.6), 0 1px 6px rgba(0, 0, 0, 0.4)' }}>
              Strategic guidance and managed services for companies navigating international trade requirements.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/services">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-50 px-9 h-14 text-base font-semibold shadow-lg">
                  Our Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 px-9 h-14 text-base font-semibold shadow-lg">
                  Request Consultation
                </Button>
              </a>
            </div>
          </div>

          {/* Value Highlights - Refined */}
          <div className="mt-28 grid grid-cols-3 gap-12 max-w-2xl">
            <div className="border-l-2 border-white/40 pl-6">
              <div className="text-base font-bold text-white mb-2 uppercase tracking-wide drop-shadow">Expert Team</div>
              <div className="text-sm text-white/90 drop-shadow">Seasoned professionals</div>
            </div>
            <div className="border-l-2 border-white/40 pl-6">
              <div className="text-base font-bold text-white mb-2 uppercase tracking-wide drop-shadow">Global Reach</div>
              <div className="text-sm text-white/90 drop-shadow">Worldwide coverage</div>
            </div>
            <div className="border-l-2 border-white/40 pl-6">
              <div className="text-base font-bold text-white mb-2 uppercase tracking-wide drop-shadow">Proven Results</div>
              <div className="text-sm text-white/90 drop-shadow">Compliance excellence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services - Modern Grid with Overlapping Cards */}
      <section className="py-28 bg-white relative">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-20 items-start mb-20">
            <div>
              <div className="text-sm font-semibold text-slate-600 uppercase tracking-widest mb-4">What We Do</div>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Comprehensive trade solutions
              </h2>
            </div>
            <div className="lg:pt-4">
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                From regulatory compliance to strategic advisory, we provide end-to-end support for international trade operations.
              </p>
              <Link to="/services" className="inline-flex items-center text-slate-700 hover:text-slate-900 font-semibold text-lg group">
                View all services
                <ChevronRight className="ml-1 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Service Cards - Sophisticated Layout */}
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              { title: 'Product Classification', desc: 'Accurate HTS, HS, ECCN, and USML classification across all global jurisdictions.', link: '/services/product-classification' },
              { title: 'Export Controls & Licensing', desc: 'ITAR and EAR compliance, license applications, and jurisdiction determinations.', link: '/services/export-controls' },
              { title: 'Foreign Trade Zones', desc: 'FTZ applications, activation, and ongoing operational support.', link: '/services/foreign-trade-zones' },
              { title: 'Voluntary Disclosures', desc: 'Strategic guidance for self-reporting violations to BIS, DDTC, and CBP.', link: '/services/voluntary-disclosures' },
              { title: 'Trade Management', desc: 'Comprehensive managed support without added headcount.', link: '/services/trade-management' },
              { title: 'Training & Audits', desc: 'Export compliance training and comprehensive program assessments.', link: '/services/training-audits' }
            ].map((service, i) => (
              <a 
                key={i}
                href={service.link}
                className="group relative bg-white border border-gray-200 hover:border-slate-300 rounded-lg p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 block"
              >
                <div className="absolute top-0 left-0 w-1 h-0 bg-slate-700 group-hover:h-full transition-all duration-300 rounded-l-lg"></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.desc}</p>
                <span className="inline-flex items-center text-slate-700 font-semibold group-hover:text-slate-900">
                  Learn more
                  <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Blackbird - Split Layout */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2">
              <div className="text-sm font-semibold text-slate-600 uppercase tracking-widest mb-6">Why Blackbird</div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Trusted by global enterprises
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-10">
                We combine deep regulatory expertise with practical operational experience, delivering compliance solutions that protect and enable your business.
              </p>
              <Link to="/about" onClick={() => window.scrollTo(0, 0)}>
                <Button className="bg-slate-700 hover:bg-slate-800 text-white px-8 h-12 text-base font-semibold">
                  About Our Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            
            <div className="lg:col-span-3">
              <div className="space-y-6">
                {[
                  { 
                    title: 'Tailored Solutions',
                    desc: 'Customized compliance strategies designed for your specific business needs and trade patterns.'
                  },
                  {
                    title: 'Responsive Service',
                    desc: 'Direct access to senior consultants who understand your business and provide timely guidance.'
                  },
                  {
                    title: 'Cost-Effective Approach',
                    desc: 'Flexible engagement models that deliver enterprise-level expertise without the overhead.'
                  }
                ].map((item, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-8 hover:border-slate-300 transition-colors">
                    <div className="text-xl font-bold text-gray-900 mb-3">{item.title}</div>
                    <div className="text-gray-600 leading-relaxed">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Full Width Premium */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight max-w-4xl mx-auto">
            Ready to optimize your global trade operations?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Connect with our trade experts to discuss your compliance needs and growth opportunities.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100 px-10 h-14 text-base font-semibold shadow-xl">
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a href="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 h-14 text-base font-semibold">
                Get in Touch
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12 mb-16 pb-16 border-b border-gray-800">
            <div className="lg:col-span-2">
              <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_blackbird-redesign/artifacts/iuwp2epg_logo-trade-black-svg.svg" 
                  alt="Blackbird Trade Group" 
                  className="h-12 mb-6 brightness-0 invert"
                />
              </Link>
              <p className="text-gray-400 leading-relaxed max-w-sm mb-6">
                Expert trade compliance and advisory services for companies navigating international regulations.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Services</h4>
              <ul className="space-y-3 text-sm">
                {[
                  { label: 'Trade Compliance', link: '/services/trade-compliance' },
                  { label: 'Trade Management', link: '/services' },
                  { label: 'Classification', link: '/services' },
                  { label: 'Audits', link: '/services' },
                  { label: 'FTZ Advisory', link: '/services' }
                ].map(item => (
                  <li key={item.label}>
                    <Link to={item.link} className="text-gray-400 hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Company</h4>
              <ul className="space-y-3 text-sm">
                {[
                  { label: 'About Us', link: '/about' },
                  { label: 'Resources', link: '/resources' },
                  { label: 'Insights', link: '/blog' },
                  { label: 'Blackbird AI', link: '/blackbird-ai' },
                  { label: 'Contact', link: '/contact' }
                ].map(item => (
                  <li key={item.label}>
                    <Link to={item.link} className="text-gray-400 hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Contact</h4>
              <div className="space-y-3 text-sm text-gray-400">
                <p>info@blackbirdtradegroup.com</p>
                <p>Miami, FL | Paris, France</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between items-center text-sm text-gray-500">
            <p>© 2024 Blackbird Trade Group. All rights reserved.</p>
            <div className="flex gap-8 mt-4 lg:mt-0">
              <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConceptRevised1;